import { React } from 'react'
import { motion } from 'framer-motion'
import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import Hero from 'modules/Hero'
import Button from 'components/Button'
import ServicesList from 'components/ServicesList'
import './ServicesPages.scss'

const TMASupport = () => {
	useDocumentTitle(`TMA & Support Web - Maintenance et Évolution de Sites`)
	useDocumentConfig(`blue`, false)

	return (
		<motion.main
			className='service-page'
			initial={{ opacity: 0, transition: { duration: 0.3 } }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0, transition: { duration: 0.3 } }}
		>
			<Hero
				scene={5}
				scrollTo='Découvrir nos solutions'
			>
				TMA &<br />
				Support
			</Hero>

			<section className='service-intro'>
				<div className='container'>
					<h1 className='align-center'>Tierce Maintenance Applicative & Support</h1>
					<p className='lead align-center'>
						Assurez la pérennité et l'évolution continue de vos applications web avec notre service de maintenance expert
					</p>
				</div>
			</section>

			<section className='service-content'>
				<div className='container'>
					<div className='content-grid'>
						<div className='content-main'>
							<h2>Un partenaire fiable pour maintenir vos sites performants</h2>
							<p>
								La Tierce Maintenance Applicative (TMA) est essentielle pour garantir la stabilité, 
								la sécurité et les performances optimales de vos applications et sites web. Notre équipe d'experts 
								prend en charge la maintenance corrective, évolutive et préventive de vos projets digitaux, 
								vous permettant de vous concentrer sur votre cœur de métier.
							</p>

							<h3>Nos services de maintenance</h3>
							<ul className='no-prefix'>
								<li><strong>Maintenance corrective</strong> : Résolution rapide des bugs et anomalies détectées</li>
								<li><strong>Maintenance évolutive</strong> : Ajout de nouvelles fonctionnalités et améliorations</li>
								<li><strong>Maintenance préventive</strong> : Mises à jour de sécurité et optimisations régulières</li>
								<li><strong>Support technique</strong> : Assistance et conseil pour vos équipes</li>
								<li><strong>Monitoring 24/7</strong> : Surveillance proactive de vos applications</li>
							</ul>

							<h3>Types d'interventions courantes</h3>
							<div className='tech-grid'>
								<div className='tech-category'>
									<h4>Corrections urgentes</h4>
									<p>Bugs critiques, erreurs 500, problèmes d'affichage, formulaires défaillants</p>
								</div>
								<div className='tech-category'>
									<h4>Optimisations</h4>
									<p>Performance, SEO, temps de chargement, score Core Web Vitals</p>
								</div>
								<div className='tech-category'>
									<h4>Mises à jour</h4>
									<p>CMS, frameworks, plugins, dépendances, certificats SSL</p>
								</div>
								<div className='tech-category'>
									<h4>Évolutions</h4>
									<p>Nouvelles pages, fonctionnalités, intégrations API, refonte partielle</p>
								</div>
							</div>

							<h3>Technologies supportées</h3>
							<ul className='no-prefix'>
								<li>CMS : WordPress, Shopify</li>
								<li>Frameworks : React, Vue.js, Angular, Next.js, Nuxt.js</li>
								<li>Back-end : PHP, Node.js, Python, Ruby on Rails</li>
								<li>Bases de données : MySQL, PostgreSQL, MongoDB</li>
								<li>Hébergement : AWS, OVH, Google Cloud, o2switch, serveurs dédiés</li>
							</ul>

							<h3>Nos engagements</h3>
							<ul className='no-prefix'>
								<li><strong>Réactivité garantie</strong> : Intervention sous 24h pour les urgences</li>
								<li><strong>Transparence totale</strong> : Rapports mensuels détaillés d'activité</li>
								<li><strong>Expertise technique</strong> : Équipe certifiée et expérimentée</li>
								<li><strong>Continuité de service</strong> : Documentation et passation complètes</li>
								<li><strong>Sécurité renforcée</strong> : Audits et correctifs de sécurité réguliers</li>
							</ul>
						</div>

						<aside className='content-sidebar'>
							<div className='sidebar-box'>
								<h3>Formules disponibles</h3>
								<ul className='benefits-list no-prefix'>
									<li>Contrat mensuel flexible</li>
									<li>Forfait heures prépayées</li>
									<li>Support à la demande</li>
									<li>Maintenance all-inclusive</li>
								</ul>
							</div>

							<div className='sidebar-box'>
								<h3>Tarifs TMA</h3>
								<p className='price-info'>
									<strong>À partir de 440€ HT/mois</strong><br />
									Forfait maintenance de base
								</p>
								<p className='small'>
									Interventions ponctuelles : 80-120€ HT/h<br />
									Devis personnalisé gratuit
								</p>
							</div>

							<div className='sidebar-box'>
								<h3>SLA garantis</h3>
								<ul className='benefits-list no-prefix'>
									<li>Urgences : intervention &lt; 4h</li>
									<li>Bugs majeurs : résolution &lt; 24h</li>
									<li>Évolutions : planning sous 48h</li>
									<li>Disponibilité : 99.9% garantie</li>
								</ul>
							</div>

							<div className='sidebar-box cta-box'>
								<h3>Audit gratuit</h3>
								<p>Évaluons ensemble les besoins de maintenance de votre site</p>
								<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1' size='small'>
									Demander un audit
								</Button>
							</div>
						</aside>
					</div>
				</div>
			</section>

			<section className='service-process'>
				<div className='container'>
					<h2 className='align-center'>Notre méthodologie TMA</h2>
					<div className='process-steps'>
						<div className='process-step'>
							<span className='step-number'>01</span>
							<h3>Audit initial</h3>
							<p>État des lieux technique et identification des besoins</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>02</span>
							<h3>Prise en main</h3>
							<p>Documentation, accès sécurisés et mise en place des outils</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>03</span>
							<h3>Maintenance active</h3>
							<p>Interventions régulières selon le contrat défini</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>04</span>
							<h3>Reporting</h3>
							<p>Bilans mensuels et recommandations d'évolution</p>
						</div>
					</div>
				</div>
			</section>

			<ServicesList currentService='tma-support' />

			<div className='column-full align-center'>
				<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1'>
					Sécuriser votre site
				</Button>
			</div>
		</motion.main>
	)
}

export default TMASupport